// src/components/InfiniteWhoIsEngineerCodewalaStyled.jsx
import React from "react";
import styled, { keyframes } from "styled-components";
import { gradients } from "./theme/theme";

/* --- Styles --- */
const Section = styled.section`
  background: ${gradients.heroBackground};
  color: #fff;
  overflow: hidden;
  padding: 4rem 0;
`;

const Title = styled.h2`
  font-size: 3rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  margin-bottom: 3rem;
`;

/* marquee animation */
const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const TrackWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  animation: ${scroll} 22s linear infinite;
  will-change: transform;

  &:hover {
    animation-play-state: paused;
  }
`;

/* Full width viewport */
const SliderViewport = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 1rem 0;
`;

/* Card */
const Card = styled.article`
  flex: 0 0 320px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.02)
  );
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.04);
  position: relative;
  transition: transform 0.35s cubic-bezier(0.2, 0.9, 0.2, 1), box-shadow 0.35s;
  backdrop-filter: blur(6px);

  &:hover {
    transform: translateY(-8px) scale(1.03);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7);
  }
`;

const Accent = styled.div`
  position: absolute;
  left: -2px;
  top: -2px;
  bottom: -2px;
  width: 6px;
  border-radius: 16px;
  background: linear-gradient(180deg, #ff7a18, #ff0066);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.6rem;
`;

const Badge = styled.span`
  background: rgba(255, 255, 255, 0.06);
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  color: #ffd9b8;
  border: 1px solid rgba(255, 255, 255, 0.03);
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  color: #fff;
`;

const CardText = styled.p`
  color: #d6d6d6;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0.6rem 0 1rem;
`;

const Stats = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  background: rgba(255, 255, 255, 0.03);
  padding: 0.35rem 0.6rem;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.02);
`;

const CTA = styled.a`
  display: inline-block;
  background: linear-gradient(90deg, #ff7a18, #ff0066);
  padding: 0.55rem 0.9rem;
  border-radius: 10px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: 0 8px 18px rgba(255, 0, 102, 0.08);
  transition: transform 0.3s;
  &:hover {
    transform: scale(1.05);
  }
`;

const Track = styled(TrackWrap)`
  padding: 1rem 0;
`;

/* --- Component --- */
export default function InfiniteWhoIsEngineerCodewalaStyled() {
  const slides = [
    {
      title: "Tech Educator",
      badge: "YouTube",
      text: "Teaching developers React Native, Web Dev, and production workflows.",
      stats: ["8k+ Subscribers", "Practical Tutorials", "Free & Paid Courses"],
      cta: "https://www.youtube.com/@engineercodewala",
      ctaLabel: "View YouTube Channel",
    },
    {
      title: "Course Provider",
      badge: "Learning",
      text: "Offering budget-friendly courses to help students & devs learn mobile and web development with hands-on projects.",
      stats: ["React Native", "Kotlin Android", "Full Stack Web"],
      cta: "/courses", // link to your courses page
      ctaLabel: "Explore Courses",
    },
    {
      title: "Senior Software Developer",
      badge: "LinkedIn",
      text: "Building scalable apps, mentoring devs, and sharing insights on software engineering.",
      stats: ["React Native", "React.js", "Node.js"],
      cta: "https://www.linkedin.com/in/gauravbhadauria1995/",
      ctaLabel: "View LinkedIn",
    },
    {
      title: "Open Source Contributor",
      badge: "GitHub",
      text: "Explore free repositories, code samples, and contributions by Engineer Codewala.",
      stats: ["React Native Projects", "Web Apps", "Libraries"],
      cta: "https://github.com/Gauravbhadauria",
      ctaLabel: "Check Free Repos",
    },
    {
      title: "Portfolio Showcase",
      badge: "Projects",
      text: "Take a look at freelance projects delivered across mobile, web, and full-stack. These are showcased as portfolio only (not for direct purchase).",
      stats: ["Mobile Apps", "Websites", "Full-stack Apps"],
      cta: "/projects", // your portfolio page
      ctaLabel: "View Portfolio",
    },
    {
      title: "Product Builder",
      badge: "Startup",
      text: "Check out SolveMyCodeIssue.com — a platform to solve dev issues & showcase projects.",
      stats: ["Hire Devs", "Post Issues", "Earn & Learn"],
      cta: "https://solvemycodeissue.com",
      ctaLabel: "View SolveMyCodeIssue",
    },
  ];

  const loopSlides = [...slides, ...slides];

  return (
    <Section aria-label="Who is Engineer Codewala">
      <Title>Who is Engineer Codewala?</Title>
      <SliderViewport>
        <Track>
          {loopSlides.map((s, i) => (
            <Card key={i}>
              <Accent />
              <CardHeader>
                <Badge>{s.badge}</Badge>
                <div>
                  <CardTitle>{s.title}</CardTitle>
                  <small style={{ color: "#a6a6a6", fontSize: 12 }}>
                    Trusted by startups & developers
                  </small>
                </div>
              </CardHeader>

              <CardText>{s.text}</CardText>

              <Stats>
                {s.stats.map((st, idx) => (
                  <Stat key={idx}>{st}</Stat>
                ))}
              </Stats>

              <div style={{ marginTop: "0.6rem" }}>
                <CTA href={s.cta} target="_blank" rel="noopener noreferrer">
                  {s.ctaLabel}
                </CTA>
              </div>
            </Card>
          ))}
        </Track>
      </SliderViewport>
    </Section>
  );
}
